import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableRow,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  IconButton,
  Box,
  Typography,
} from "@material-ui/core";
import MovableMasterService from "./Locality/Service/movableMasterService";
import * as Yup from 'yup';
import TablePagination from "@material-ui/core/TablePagination";
import { Grid, TextField } from "@material-ui/core";
import { useFormik } from 'formik';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Widget from "../../components/Widget/Widget";
import PageTitle from "../../components/PageTitle/PageTitle";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import { showAppConfirm } from '../../context/AlertContext';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#1a1a1aad",
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
  tableOverflow: {
    overflowX: "auto",
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
}));

export default function MovableMaster() {
  const tableHeaders = ['Movable Alphabet Name', 'Edit', 'Delete'];
  const classes = useStyles();
  const [movableMasterList, setMovableMasterList] = useState([]);
  const [pg, setPg] = useState(0);
  const [rpg, setRpg] = useState(10);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState(null);

  const [movableMaster, setMovableMaster] = useState({
    name: '',
    schooleId: '',
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Movable Alphabet Name is required'),
    schooleId: Yup.string(),
  });

  const formik = useFormik({
    initialValues: movableMaster,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (isEditMode) {
        updateMovableMaster();
      } else {
        addMovableMaster();
      }
    },
  });

  useEffect(() => {
    getMovableMasterList();
    return () => {
      setMovableMasterList([]);
    };
  }, []);

  const getMovableMasterList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
    const body = {};
    
    if (userDetails.schooleId) {
      body.schooleId = userDetails.schooleId;
    }
    
    MovableMasterService.findMovableMasterList(body)
      .then((res) => {
        setMovableMasterList(res);
      })
      .catch((err) => {
        console.error("Error fetching movable master list:", err);
        setError(err.message);
      });
  };


  const handleChangePage = (event, newPage) => {
    setPg(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRpg(parseInt(event.target.value, 10));
    setPg(0);
  };

  const handleOpen = (status) => {
    if (status) {
      const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const resetData = {
        name: '',
        schooleId: userDetails.schooleId || '',
      };
      setMovableMaster(resetData);
      setIsEditMode(false);
      // Reset Formik form to clear previous data
      formik.resetForm({
        values: resetData
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditMode(false);
  };


  const addMovableMaster = async () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const body = {
        name: formik.values.name,
        schooleId: userDetails.schooleId || formik.values.schooleId,
      };

      await MovableMasterService.createMovableMaster(body);
      alert("Movable Master added successfully!");
      handleClose();
      // Refresh the list after a short delay to ensure state is updated
      setTimeout(() => {
        getMovableMasterList();
      }, 100);
    } catch (err) {
      console.error("Error adding movable master:", err);
      alert(err.response?.data?.message || "Error adding movable master");
    }
  };

  // Helper function to check if user is superAdmin
  const isSuperAdmin = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
    return userDetails.role === "superAdmin" || userDetails.role === "SUPERADMIN";
  };

  // Helper function to check if schooleId exists and is valid
  const hasSchooleId = (item) => {
    if (!item) return false;
    const schooleId = item.schooleId;
    // Check if schooleId exists and is not null/undefined/empty
    // Handle both ObjectId strings and populated objects
    if (schooleId === null || schooleId === undefined || schooleId === '') {
      return false;
    }
    // If it's an object (populated), check if it has an _id
    if (typeof schooleId === 'object' && schooleId._id) {
      return true;
    }
    // If it's a string (ObjectId), it's valid
    if (typeof schooleId === 'string' && schooleId.length > 0) {
      return true;
    }
    return false;
  };

  // Helper function to check if edit/delete is allowed
  const canEditOrDelete = (item) => {
    // SuperAdmin can edit/delete any record (even with null schooleId)
    if (isSuperAdmin()) {
      return true;
    }
    // Other users can only edit/delete records with schooleId
    return hasSchooleId(item);
  };

  const editMovableMaster = (movableMasterData) => {
    // Only allow editing if schooleId exists or user is superAdmin
    if (!canEditOrDelete(movableMasterData)) {
      alert("Cannot edit global records. Only school-specific records can be edited.");
      return;
    }

    const obj = {
      _id: movableMasterData._id,
      name: movableMasterData.name || '',
      schooleId: movableMasterData.schooleId?._id || movableMasterData.schooleId || '',
    };

    setMovableMaster(obj);
    setIsEditMode(true);
    setOpen(true);
  };

  const updateMovableMaster = async () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const body = {
        name: formik.values.name,
        schooleId: userDetails.schooleId || formik.values.schooleId,
      };

      await MovableMasterService.updateMovableMaster(movableMaster._id, body);
      alert("Movable Master updated successfully!");
      handleClose();
      // Refresh the list after a short delay to ensure state is updated
      setTimeout(() => {
        getMovableMasterList();
      }, 100);
    } catch (err) {
      console.error("Error updating movable master:", err);
      alert(err.response?.data?.message || "Error updating movable master");
    }
  };

  const deleteMovableMaster = async (movableMasterDelete) => {
    if (!movableMasterDelete) {
      return;
    }

    // Only allow deleting if schooleId exists or user is superAdmin
    if (!canEditOrDelete(movableMasterDelete)) {
      alert("Cannot delete global records. Only school-specific records can be deleted.");
      return;
    }

    const confirmDelete = await showAppConfirm(
      "Are you sure you want to delete this Movable Master record?"
    );
    
    if (!confirmDelete) {
      return;
    }

    MovableMasterService.deleteMovableMaster(movableMasterDelete._id)
      .then((res) => {
        alert("Movable Master deleted successfully!");
        getMovableMasterList();
      })
      .catch((err) => {
        console.error("Error deleting movable master:", err);
        alert(err.response?.data?.message || "Error deleting movable master");
      });
  };


  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={10}>
          <PageTitle title="Movable Master" />
        </Grid>
        <Grid item xs={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpen(true)}
            style={{ marginTop: '10px' }}
          >
            Add Movable Master
          </Button>
        </Grid>
      </Grid>

      <Box style={{ marginBottom: 16, marginTop: 8, padding: 12, backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 4 }}>
        <Typography variant="body2" color="textSecondary">
          By default, all Phonics, Cursive, and movable are added. If you want to add extra fields, add them here.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Widget
            title="Movable Master List"
            upperTitle
            noBodyPadding
            bodyClass={classes.tableOverflow}
          >
            <TableContainer component={Paper} style={{ maxHeight: 500 }}>
              <Table stickyHeader className="mb-0 dashboard-table-row-hover">
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <StyledTableCell key={header}>{header}</StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movableMasterList
                    .slice(pg * rpg, pg * rpg + rpg)
                    .map((movableMasterItem) => (
                      <TableRow key={movableMasterItem._id}>
                        <TableCell className="pl-3 fw-normal">
                          {movableMasterItem.name || 'N/A'}
                        </TableCell>
                        <TableCell className="pl-3 fw-normal">
                          <IconButton
                            color="primary"
                            onClick={() => editMovableMaster(movableMasterItem)}
                            aria-label="edit"
                            disabled={!canEditOrDelete(movableMasterItem)}
                            style={{ 
                              opacity: canEditOrDelete(movableMasterItem) ? 1 : 0.5,
                              cursor: canEditOrDelete(movableMasterItem) ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </TableCell>
                        <TableCell className="pl-3 fw-normal">
                          <IconButton
                            color="secondary"
                            onClick={() => deleteMovableMaster(movableMasterItem)}
                            aria-label="delete"
                            disabled={!canEditOrDelete(movableMasterItem)}
                            style={{ 
                              opacity: canEditOrDelete(movableMasterItem) ? 1 : 0.5,
                              cursor: canEditOrDelete(movableMasterItem) ? 'pointer' : 'not-allowed'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              count={movableMasterList.length}
              page={pg}
              onPageChange={handleChangePage}
              rowsPerPage={rpg}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Widget>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          style: {
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle id="form-dialog-title">
          {isEditMode ? "Edit Movable Master" : "Add Movable Master"}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  autoFocus
                  margin="normal"
                  id="name"
                  name="name"
                  label="Movable Alphabet Name"
                  type="text"
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" color="primary" variant="contained">
              {isEditMode ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

